import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api from "../../lib/api";
import DiscoverLayout from "../../components/discover/DiscoverLayout";
import StartupCard from "../../components/discover/StartupCard";
import SearchBar from "../../components/discover/SearchBar";
import FilterBar from "../../components/discover/FilterBar";
import Pagination from "../../components/discover/Pagination";
import { motion } from "framer-motion";

export default function BrowseStartupsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  const [activeFilters, setActiveFilters] = useState({
    industry: "All",
    location: "All",
  });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/discover/startups?limit=100")
      .then((r) => {
        if (alive) {
          setItems(r.data.items || []);
        }
      })
      .catch((e) => console.error("Error loading startups:", e))
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, []);

  const handleFilterChange = (key, value) => {
    setActiveFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const getFilterOptions = (key) => {
    const vals = items.map((item) => {
      if (key === "location") return item.location ? item.location.split(",")[0].trim() : "";
      return item[key];
    }).filter(Boolean);
    return Array.from(new Set(vals)).sort();
  };

  const industriesList = getFilterOptions("industry");
  const locationsList = getFilterOptions("location");

  const filters = [
    { key: "industry", label: "Industry", options: industriesList },
    { key: "location", label: "Location", options: locationsList },
  ];

  const filtered = items.filter((item) => {
    const q = searchQuery.toLowerCase().trim();
    const matchesSearch =
      !q ||
      item.name.toLowerCase().includes(q) ||
      (item.description && item.description.toLowerCase().includes(q)) ||
      (item.industry && item.industry.toLowerCase().includes(q));

    const matchesIndustry =
      activeFilters.industry === "All" || item.industry === activeFilters.industry;

    const matchesLocation =
      activeFilters.location === "All" ||
      (item.location && item.location.toLowerCase().includes(activeFilters.location.toLowerCase()));

    return matchesSearch && matchesIndustry && matchesLocation;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedItems = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <DiscoverLayout
      title="Browse Startups"
      subtitle="Large searchable directory of the startup ecosystem. Use alphabetical indexes and advanced criteria to inspect businesses."
    >
      <div className="space-y-6">


        {/* Search & Stats */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="w-full md:w-96">
            <SearchBar
              value={searchQuery}
              onChange={(q) => {
                setSearchQuery(q);
                setCurrentPage(1);
              }}
              placeholder="Search directory..."
            />
          </div>
          <span className="text-xs text-gray-500 font-bold">
            Showing {filtered.length} startups in directory
          </span>
        </div>

        {/* Filters */}
        <FilterBar
          filters={filters}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
        />

        {/* Grid List */}
        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#7C3AED]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 rounded-2xl border border-dashed border-gray-200 bg-white shadow-sm">
            <p className="text-sm text-gray-500 font-medium">No directory entries match your selection.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedItems.map((c) => (
                <StartupCard key={c.slug} c={c} />
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>
    </DiscoverLayout>
  );
}
